import React from 'react';
import { Box, Card, Grid, Skeleton, Stack, useTheme } from '@mui/material';

const BORDER_RADIUS = '10px';

const GRID_SPACING = { xs: 1, md: 2 };
const SECTION_GAP = { xs: 2, md: 4.5 };

const SentimentsSkeleton = () => {
  const theme = useTheme();

  const SkeletonStatsCard = () => (
    <Card
      sx={{
        height: '100%',
        minHeight: { xs: '100px', sm: '160px' },
        borderRadius: BORDER_RADIUS,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        boxShadow: 'none',
        p: { xs: 1.25, sm: 1.75, md: 2.5 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      <Box display="flex" justifyContent="space-between">
        <Skeleton variant="text" width={70} height={20} />
        <Skeleton variant="circular" width={14} height={14} />
      </Box>

      <Box>
        <Skeleton variant="text" width="60%" height={36} sx={{ mt: { xs: 0.75, md: 2 } }} />
      </Box>

      <Box display="flex" alignItems="center" gap={{ xs: 0.5, md: 1 }}>
        <Skeleton variant="circular" width={18} height={18} />
        <Skeleton variant="text" width="40%" height={18} />
      </Box>
    </Card>
  );

  const SkeletonCurrencyCard = () => (
    <Card
      sx={{
        height: '100%',
        minHeight: { xs: '100px', sm: '160px' },
        borderRadius: BORDER_RADIUS,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        p: { xs: 1.25, sm: 1.75, md: 2.5 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      <Stack spacing={{ xs: 0.4, md: 0.8 }}>
        <Skeleton variant="text" width={36} height={20} />
        <Skeleton variant="text" width={56} height={28} />
      </Stack>

      <Skeleton variant="rectangular" height={12} sx={{ borderRadius: 3, mt: { xs: 1, md: 2 } }} />
    </Card>
  );

  const SkeletonHotPairCard = () => (
    <Card
      sx={{
        height: '100%',
        minHeight: { xs: 80, md: 100 },
        borderRadius: BORDER_RADIUS,
        bgcolor: theme.palette.action.hover,
        border: 'none',
        boxShadow: 'none',
        p: { xs: 1.25, md: 2.25 }
      }}
    >
      <Stack spacing={{ xs: 1, md: 2 }}>
        <Skeleton variant="text" width={50} height={20} />
        <Skeleton variant="text" width={36} height={28} />
      </Stack>
    </Card>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="column" gap={SECTION_GAP}>
        <Grid container spacing={GRID_SPACING}>
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: '16px',
                border: `1px solid ${theme.palette.divider}`,
                p: { xs: 1.25, md: 2 },
                minHeight: { xs: '100%', md: 320 }
              }}
            >
              <Skeleton variant="text" width={280} height={40} sx={{ bgcolor: theme.palette.action.hover }} />
              <Grid container spacing={GRID_SPACING} p={{ xs: 0, md: 2 }}>
                {[...Array(4)].map((_, i) => (
                  <Grid item xs={6} key={i}>
                    <SkeletonStatsCard />
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: '16px',
                border: `1px solid ${theme.palette.divider}`,
                p: { xs: 1.25, md: 2 },
                minHeight: 380
              }}
            >
              <Box pl={{ xs: 0, md: 2 }}>
                <Skeleton variant="text" width={280} height={40} sx={{ bgcolor: theme.palette.action.hover }} />
              </Box>
              <Grid container spacing={GRID_SPACING} p={{ xs: 0, md: 2 }}>
                {[...Array(8)].map((_, i) => (
                  <Grid item xs={6} sm={3} md={6} lg={3} key={i}>
                    <SkeletonCurrencyCard />
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={GRID_SPACING}>
          <Grid item xs={12} md={7}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: '16px',
                border: `1px solid ${theme.palette.divider}`,
                p: { xs: 1.25, md: 2 },
                minHeight: 380
              }}
            >
              <Box pb={2}>
                <Skeleton variant="text" width={280} height={40} sx={{ bgcolor: theme.palette.action.hover }} />
              </Box>
              <Box display="flex" justifyContent="space-between" mb={2}>
                {[...Array(8)].map((_, i) => (
                  <Skeleton key={i} width={30} height={20} />
                ))}
              </Box>
              {[...Array(7)].map((_, i) => (
                <Box key={i} display="flex" justifyContent="space-between" mb={1}>
                  {[...Array(7)].map((_, j) => (
                    <Skeleton key={j} variant="rectangular" width="100%" height={32} sx={{ mx: 0.4, borderRadius: 1 }} />
                  ))}
                </Box>
              ))}
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: '16px',
                border: `1px solid ${theme.palette.divider}`,
                p: { xs: 1.25, md: 2 },
                minHeight: 380
              }}
            >
              <Box pb={2}>
                <Skeleton variant="text" width={280} height={40} sx={{ bgcolor: theme.palette.action.hover }} />
              </Box>
              <Grid container spacing={GRID_SPACING}>
                {[...Array(9)].map((_, i) => (
                  <Grid item xs={6} sm={4} md={6} lg={4} key={i}>
                    <SkeletonHotPairCard />
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

export default SentimentsSkeleton;
